import React, { useState, useCallback, useEffect, useRef } from 'react';
import debounce from 'lodash/debounce';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Icon, Button } from '@cbrebuild/blocks';
import * as adminService from '../../services/admin-service';
import userEventService from '../../services/user-event-service';

import EmptyState from '../../components/empty-state/empty-state';

// Import the new smaller components
import AdminDealSearch from './admin-deal-search';
import AdminDealDetails from './admin-deal-details';
import AdminCommissionOverview from './admin-commission-overview';

// Import existing working commission overview component
import CommissionsOverview from '../../components/deal-details/commissions/commissions-overview';
import CommissionsDetailTabs from '../../components/deal-details/commissions/commissions-detail-tabs/commissions-detail-tabs';
import CommissionsSummary from '../../components/deal-details/commissions/commissions-summary';
import CommissionsDetail from '../../components/deal-details/commissions/commissions-detail';

// Import user deal refresh component
import AdminUserDealRefresh from './admin-user-deal-refresh';
import AdminDealRestoreSearch from './admin-deal-search-restore';

// Import deal team components from the regular deal overview
import DealMembers from '../../components/headers/deal-detail-header/deal-members';
import {
  addAllAdminDealMembers,
  addCommissionTeams,
  removeCommissionTeams,
  addAllAdminCommissionMembers,
  removeAdminCommissionMembers,
} from '../../redux/actions/team-members-actions';
import superFetch from '../../utils/super-fetch';
import { formatDealTypeAndRole, findLinkedDealId, formatDealStage, prepareVoucherData, prepareCommissionUsersData } from '../../utils/deal-display-utils';
import { loadAdminManageDealSuccess, loadAdminManageDealFailure } from '../../redux/actions/admin-actions';
import './admin-manage-deal.scss';
import Accordion from '../../components/accordion/accordion';

const AdminManageDeal = (props) => {
  // Add ref to track if component is mounted to prevent memory leaks
  
  const { 
    userData, 
    toast, 
    loadAdminDealMembers,
    dealUsers,
    dealTeams,
    relatedTransactionUsers,
    reduxDeal,
    setAdminManageDealSuccess,
    setAdminManageDealFailure,
    loadAdminCommissionMembers,
    addAllAdminDealMembers,
    addCommissionTeams,
    removeCommissionTeams,
    addAllAdminCommissionMembers,
    removeAdminCommissionMembers
  } = props;
  
  const [searchResults, setSearchResults] = useState([]);
  const [expandedDealId, setExpandedDealId] = useState(null);
  const [isSearching, setIsSearching] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalResults, setTotalResults] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loadingTeamData, setLoadingTeamData] = useState(null);
  const [commissionOverviewData, setCommissionOverviewData] = useState(null);
  const [loadingTransactionId, setLoadingTransactionId] = useState(null);
  const [selectedCommissionTransactionId, setSelectedCommissionTransactionId] = useState(null);
  const [commissionMembersData, setCommissionMembersData] = useState(null);
  
  // Additional commission data state
  const [considerations, setConsiderations] = useState([]);
  const [additionalCommissions, setAdditionalCommissions] = useState([]);
  const [installments, setInstallments] = useState([]);
  const [escalations, setEscalations] = useState([]);
  
  // Voucher submission state
  const [showVoucherSubmissions, setShowVoucherSubmissions] = useState(null);
  
  // Cleanup effect to prevent memory leaks
  useEffect(() => {
 // API trigger
  }, []);
  
  // Clear loading state when team data is received
  useEffect(() => {
    if (((dealUsers !== undefined && Array.isArray(dealUsers)) || 
        (dealTeams !== undefined && Array.isArray(dealTeams)) ||
        (relatedTransactionUsers !== undefined && Array.isArray(relatedTransactionUsers)))) {
      setLoadingTeamData(null);
    }
  }, [dealUsers, dealTeams, relatedTransactionUsers]);

  // Capture commission members data when relatedTransactionUsers is updated
  useEffect(() => {
    if (selectedCommissionTransactionId && relatedTransactionUsers && 
        Array.isArray(relatedTransactionUsers)) {
      setCommissionMembersData(relatedTransactionUsers);
    }
  }, [relatedTransactionUsers, selectedCommissionTransactionId]);

  // Function to get unique commission users
  const getUniqueCommissionUsers = () => {
    if (!commissionMembersData || !Array.isArray(commissionMembersData)) {
      return [];
    }
    
    const initialDealUserIds = Array.isArray(dealUsers) 
      ? dealUsers.map(dealUser => dealUser?.user?.id).filter(Boolean) 
      : [];
    
    const uniqueCommissionUsers = commissionMembersData.filter(commissionUser => {
      const userId = commissionUser?.user?.id;
      return userId && !initialDealUserIds.includes(userId);
    });
    
    return uniqueCommissionUsers;
  };

  // Function to get all commission user IDs for showing $ icon in team members
  const getCommissionUserIds = (dealData) => {
    
    // Try the original commissions approach first
    if (dealData && dealData.commissions && Array.isArray(dealData.commissions) && Array.isArray(dealUsers)) {
      const commissionBrokerEmails = dealData.commissions
        .map(commission => commission?.broker?.work_email || commission?.broker?.email)
        .filter(Boolean)
        .map(email => email.toLowerCase()); // Convert to lowercase for comparison
      
      const commissionUserIds = [];
      dealUsers.forEach(dealUser => {
        const userWorkEmail = dealUser?.user?.email?.work || dealUser?.user?.email;
        const userId = dealUser?.user?.id;

        if (userId && userWorkEmail &&  typeof userWorkEmail === 'string' && commissionBrokerEmails.includes(userWorkEmail.toLowerCase())) {
          commissionUserIds.push(userId);
        }
      });
      
      if (commissionUserIds.length > 0) {
        return commissionUserIds;
      }
    }
    
    // Try alternative approach - check if there are commission_users directly
    if (dealData && dealData.commission_users && Array.isArray(dealData.commission_users)) {
      const commissionUserIds = dealData.commission_users
        .map(user => user?.id || user?.user?.id)
        .filter(Boolean);
      return commissionUserIds;
    }
    
    // Try looking at dealUsers with commission_roles  
    if (Array.isArray(dealUsers)) {
      const commissionUserIds = dealUsers
        .filter(dealUser => dealUser && dealUser.commission_roles && dealUser.commission_roles.length > 0)
        .map(dealUser => dealUser.user ? dealUser.user.id : dealUser.id)
        .filter(Boolean);
      if (commissionUserIds.length > 0) {
        return commissionUserIds;
      }
    }
    
    return [];
  };

  // Helper function to format dates consistently
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch (error) {
      return 'Invalid Date';
    }
  };

  // Helper function to format currency consistently
  const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return '$0';
    const numericAmount = parseFloat(amount);
    if (isNaN(numericAmount)) return '$0';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(numericAmount);
  };

  const handleDealSelection = (deal) => {
    if (!deal || !deal.id) return;
    if (expandedDealId === deal.id) {
      setExpandedDealId(null);
    } else {
      setExpandedDealId(deal.id);
      
      // Track deal selection
      userEventService.trackEvent({
        eventCategory: 'Admin Capabilities',
        eventAction: 'admin_deal_selected',
        eventLabel: 'admin_manage_deal',
      });
      
      // Clear commission data when expanding a different deal
      if (selectedCommissionTransactionId || commissionOverviewData) {
        setSelectedCommissionTransactionId(null);
        setCommissionOverviewData(null);
        setCommissionMembersData(null);
        setConsiderations([]);
        setAdditionalCommissions([]);
        setInstallments([]);
        setEscalations([]);
      }
      
      // Clear voucher submissions when expanding a different deal
      if (showVoucherSubmissions) {
        setShowVoucherSubmissions(null);
      }
      deal?.id && loadAdminDealMembers({ deal: deal.id, page_size: 999 });

    }
  };

  const searchDeals = useCallback(
    debounce(async (searchTerm, page = 1) => {
      if (!searchTerm || searchTerm.trim().length < 3) {
        setSearchResults([]);
        setIsSearching(false);
        setTotalResults(0);
        setHasMore(false);
        return;
      }

      setIsSearching(true);
      
      // Track search event
      if (page === 1) {
        userEventService.trackEvent({
          eventCategory: 'Admin Capabilities',
          eventAction: 'admin_deal_search',
          eventLabel: 'admin_manage_deal',
        });
      }

      try {
        const response = await adminService.searchDealsForManagement({
          search: searchTerm.trim(),
          page_size: 25,
          page: page,
        });

        const results = response.results || [];
        setSearchResults(prev => (page === 1 ? results : [...prev, ...results]));
        setTotalResults(response.count || 0);
        setHasMore(Boolean(response.next));
        setCurrentPage(page);
        setAdminManageDealSuccess(results);
      } catch (error) {
        console.error('Error searching deals');
        setSearchResults([]);
        setTotalResults(0);
        setHasMore(false);
        setAdminManageDealFailure(error);
      } finally {
        setIsSearching(false);
      }
    }, 300),
    []
  );

  const handleSearchChange = (searchTerm) => {
    setExpandedDealId(null);
    setCurrentPage(1);
    
    // Clear commission data when starting a new search
    setSelectedCommissionTransactionId(null);
    setCommissionOverviewData(null);
    setCommissionMembersData(null);
    setConsiderations([]);
    setAdditionalCommissions([]);
    setInstallments([]);
    setEscalations([]);
    setShowVoucherSubmissions(null);
    
    searchDeals(searchTerm, 1);
  };

  // Handler for linked transaction click: fetch and show commission overview inline
  const handleLinkedMtaTransactionClick = async (transactionId) => {
    try {
        setLoadingTransactionId(transactionId);
        setSelectedCommissionTransactionId(transactionId);
        
        loadAdminCommissionMembers({ deal: transactionId, page_size: 999 });
      
      
      // Fetch all the commission data parallelly
      const [
        financialsResponse,
        considerationsResponse,
        additionalCommissionsResponse,
        installmentsResponse,
        escalationsResponse
      ] = await Promise.all([
        superFetch(`/admin/manage-deals/financials?transaction=${transactionId}`, { method: 'GET' }),
        superFetch(`/admin/manage-deals/considerations?transaction=${transactionId}&is_mta=true`, { method: 'GET' }),
        superFetch(`/admin/manage-deals/additional_commissions?transaction=${transactionId}&is_mta=true`, { method: 'GET' }),
        superFetch(`/admin/manage-deals/installments?transaction=${transactionId}&is_mta=true&ordering=-installment_date`, { method: 'GET' }),
        superFetch(`/admin/manage-deals/escalations?transaction=${transactionId}`, { method: 'GET' })
      ]);

      // Parse JSON from each response
      const [
        financialsData,
        considerationsData,
        additionalCommissionsData,
        installmentsData,
        escalationsData
      ] = await Promise.all([
        financialsResponse.json(),
        considerationsResponse.json(),
        additionalCommissionsResponse.json(),
        installmentsResponse.json(),
        escalationsResponse.json()
      ]);

        const financialData = financialsData?.results?.[0] || financialsData;
        setCommissionOverviewData(financialData);
        
        setConsiderations(considerationsData?.results || []);
        setAdditionalCommissions(additionalCommissionsData?.results || []);
        setInstallments(installmentsData?.results || []);
        setEscalations(escalationsData?.results || []);
        
        setLoadingTransactionId(null);
    } catch (error) {
        setLoadingTransactionId(null);
        setCommissionOverviewData(null);
        setConsiderations([]);
        setAdditionalCommissions([]);
        setInstallments([]);
        setEscalations([]);
        toast('Failed to load commission overview', 'error');
    }
  };

  // Handler for voucher submissions button click
  const handleVoucherSubmissionsClick = (dealId) => {
    if (showVoucherSubmissions === dealId) {
      // If clicking on the same deal, close the voucher submissions
      setShowVoucherSubmissions(null);
    } else {
      // If clicking on a different deal or opening for first time, show voucher submissions
      setShowVoucherSubmissions(dealId);
    }
  };

  // Render commission overview section with commission users
  const renderCommissionOverviewSection = (deal) => {
    if (!selectedCommissionTransactionId || !commissionOverviewData ||
        !deal.linked_mta_transactions?.some(tx => tx.transaction_id === selectedCommissionTransactionId)) {
      return null;
    }

    return (
      <div className="commissions-tab admin-commissions-tab-inline">
        <div className="commission-tab-header">
          <div className="commissions-id-and-permissions">
            <div className="commissions-deal-id">
              Commissions - Deal ID: {findLinkedDealId(deal.linked_mta_transactions, selectedCommissionTransactionId)}
            </div>
            <div className="commission-permissions">
              <div className="commission-collaborators-section">
                {(() => {
                  const uniqueCommissionUsers = getUniqueCommissionUsers();
                  const commissionData = prepareCommissionUsersData(uniqueCommissionUsers);
                  
                  return commissionData.hasUsers ? (
                    <DealMembers
                      dealUsers={commissionData.users.map(commissionUser => ({
                        user: commissionUser.user,
                        locked: false,
                        role: commissionUser.role || 'Commission User',
                        source: 'commission_api',
                        id: commissionUser.id
                      }))}
                      dealTeams={[]}
                      showAddOrRemove={true}
                      maxAvatarsToShow={8}
                      usersToSuppress={[]}
                      teamsToSuppress={[]}
                      commissionedUserIds={commissionData.users.map(c => c.user?.id).filter(Boolean)}
                      relatedTransactionId={selectedCommissionTransactionId}
                      analyticProperties={{
                        categoryPrefix: 'Admin Manage Deal',
                        actionPrefix: 'commission_members'
                      }}
                      addDealMembers={(userIds) => {
                        if (!selectedCommissionTransactionId) return;
                        addAllAdminCommissionMembers(selectedCommissionTransactionId, userIds);
                      }}
                      addDealTeams={addCommissionTeams}
                      removeDealMembers={(dealMemberIds) => {
                        if (!selectedCommissionTransactionId) return;
                        removeAdminCommissionMembers(dealMemberIds, selectedCommissionTransactionId);
                      }}
                      removeDealTeams={removeCommissionTeams}
                    />
                  ) : (
                    <div className="commission-empty-state">
                      <span className="commission-collaborators-label">Add Commission View Users</span>
                      <DealMembers
                        dealUsers={[]}
                        dealTeams={[]}
                        showAddOrRemove={true}
                        maxAvatarsToShow={0}
                        usersToSuppress={[]}
                        teamsToSuppress={[]}
                        commissionedUserIds={[]}
                        relatedTransactionId={selectedCommissionTransactionId}
                        analyticProperties={{
                          categoryPrefix: 'Admin Manage Deal',
                          actionPrefix: 'commission_members'
                        }}
                        addDealMembers={(userIds) => {
                          if (!selectedCommissionTransactionId) return;
                          addAllAdminCommissionMembers(selectedCommissionTransactionId, userIds);
                        }}
                        removeDealMembers={(dealMemberIds) => {
                          if (!selectedCommissionTransactionId) return;
                          removeAdminCommissionMembers(dealMemberIds, selectedCommissionTransactionId);
                        }}
                      />
                    </div>
                  );
                })()}
              </div>
            </div>
          </div>
        </div>
        
        <button
          className="commission-close-btn"
          title="Close commission overview"
          onClick={() => {
            setSelectedCommissionTransactionId(null);
            setCommissionOverviewData(null);
            setCommissionMembersData(null);
            setConsiderations([]);
            setAdditionalCommissions([]);
            setInstallments([]);
            setEscalations([]);
          }}
        >×</button>
        
        <div className="detail-panels-layout admin-detail-panels-padding">
          <div className="col-two-thirds">
            {commissionOverviewData?.financial_details?.commission_overview_table && (
              <CommissionsOverview data={commissionOverviewData.financial_details.commission_overview_table} />
            )}
            {installments && considerations && escalations && additionalCommissions && (
              <CommissionsDetailTabs
                additionalCommissions={additionalCommissions}
                considerations={considerations}
                escalations={escalations}
                installments={installments}
              />
            )}
          </div>
          <div className="col-one-third">
            {commissionOverviewData?.financial_details?.my_commission_summary && (
              <CommissionsSummary myCommissionSummary={commissionOverviewData.financial_details.my_commission_summary} />
            )}
            {commissionOverviewData?.financial_details?.deal_financial_overview && (
              <CommissionsDetail myCommissionDetail={commissionOverviewData.financial_details.deal_financial_overview} />
            )}
          </div>
        </div>
      </div>
    );
  };

  // Accordion sections
  const accordionSections = [
    {
      title: 'Search for Deals',
      content: (
        <AdminDealSearch
          searchResults={searchResults}
          isSearching={isSearching}
          totalResults={totalResults}
          hasMore={hasMore}
          currentPage={currentPage}
          expandedDealId={expandedDealId}
          handleSearchChange={handleSearchChange}
          handleDealSelection={handleDealSelection}
          searchDeals={searchDeals}
          renderExpandedDetails={(deal) => (
            <AdminDealDetails
              deal={deal}
              expandedDealId={expandedDealId}
              loadingTeamData={loadingTeamData}
              dealUsers={dealUsers}
              dealTeams={dealTeams}
              reduxDeal={reduxDeal}
              addAllAdminDealMembers={addAllAdminDealMembers}
              getCommissionUserIds={getCommissionUserIds}
              loadingTransactionId={loadingTransactionId}
              selectedTransactionId={selectedCommissionTransactionId}
              handleLinkedMtaTransactionClick={handleLinkedMtaTransactionClick}
              formatDate={formatDate}
              showVoucherSubmissions={showVoucherSubmissions}
              handleVoucherSubmissionsClick={handleVoucherSubmissionsClick}
              userData={userData}
              toast={toast}
              commissionOverviewSection={renderCommissionOverviewSection}
            />
          )}
        />
      ),
    },
    {
      title: 'Deals Refresh',
      content: <AdminUserDealRefresh toast={toast} />,
    },
    {
      title: 'Deals Restoration',
      content: <AdminDealRestoreSearch />,
    },
  ];

  return (
    <div className="admin-manage-deal">
      <Accordion sections={accordionSections} />
    </div>
  );
};

AdminManageDeal.propTypes = {
  userData: PropTypes.shape({
    employee_work_email: PropTypes.string,
  }).isRequired,
  toast: PropTypes.func.isRequired,
  dealUsers: PropTypes.arrayOf(PropTypes.shape()),
  dealTeams: PropTypes.arrayOf(PropTypes.shape()),
  relatedTransactionUsers: PropTypes.arrayOf(PropTypes.shape()),
  loadAdminDealMembers: PropTypes.func.isRequired,
  addAllAdminCommissionMembers: PropTypes.func.isRequired,
  removeAdminCommissionMembers: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => ({
  reduxDeal: state.deal.deal,
});

const mapDispatchToProps = dispatch => ({
  setAdminManageDealSuccess: params => dispatch(loadAdminManageDealSuccess(params)),
  setAdminManageDealFailure: params => dispatch(loadAdminManageDealFailure(params)),
});

export default connect(mapStateToProps, mapDispatchToProps)(AdminManageDeal);
